import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { providerName, useEmail } from "../lib/email";
import { ErrorText, Loading } from "./ui";

interface Request { method: "PUT" | "POST" | "PATCH"; path: string; body: Record<string, unknown>; merge?: { from: string; pick: string; keep?: string[]; wrap?: string } }
interface Card { type: "change" | "connect" | "link"; title?: string; lines?: string[]; request?: Request; connect?: "email" | "whatsapp"; page?: string; label?: string }
interface Msg { id: string; role: "user" | "assistant"; content: string; cards: Card[]; created_at: string }
interface History { messages: Msg[]; usage: { used: number; limit: number | null } }

// The only requests the assistant may propose (the server uses the same list).
const allowed = new Set(["PUT bookings/hours", "POST bookings/services", "POST bookings/resources", "PUT bookings/settings", "PUT brand", "PUT booking-page", "POST knowledge/sources"]);

const pageRoute: Record<string, string> = {
  calendar_setup: "services", services: "services", booking_page: "booking-page", calendar: "calendar", brand: "settings/brand", email: "settings/email", channels: "channels", knowledge: "knowledge",
  agents: "agents", automations: "automations", team: "team", security: "settings/security", workspace: "settings/workspace", contacts_import: "contacts/import",
  inbox: "inbox", deals: "deals", results: "results",
};

function appliedKey(orgId: string) { return `svx-assistant-applied:${orgId}`; }
function loadApplied(orgId: string): string[] {
  try { return JSON.parse(localStorage.getItem(appliedKey(orgId)) ?? "[]"); } catch { return []; }
}

const examples = ["We're open Sunday to Thursday, 9 to 5, with a lunch break 12 to 1", "Add a 30-minute consultation for 15 BHD", "Set up my email", "Make our colours dark green"];

/** Assistant is the setup helper in the side panel. It proposes; the person applies. */
export function Assistant({ onClose }: { onClose: () => void }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "assistant"];
  const hist = useQuery({ queryKey: key, queryFn: () => api<History>(`${base}/assistant`) });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [applied, setApplied] = useState<string[]>(() => loadApplied(orgId));
  const log = useRef<HTMLDivElement>(null);
  const messages = hist.data?.messages ?? [];
  useEffect(() => { log.current?.scrollTo({ top: log.current.scrollHeight }); }, [messages.length, sending]);

  function markApplied(id: string) {
    const next = [...applied.filter((x) => x !== id), id].slice(-300);
    setApplied(next);
    try { localStorage.setItem(appliedKey(orgId), JSON.stringify(next)); } catch { /* per-browser convenience only */ }
  }

  async function send(body: string) {
    const msg = body.trim();
    if (!msg || sending) return;
    setSending(true); setError(null); setText("");
    qc.setQueryData<History>(key, (h) => h && { ...h, messages: [...h.messages, { id: "pending", role: "user", content: msg, cards: [], created_at: new Date().toISOString() }] });
    try {
      await api<Msg>(`${base}/assistant/messages`, { method: "POST", json: { message: msg } });
    } catch (e) {
      setError(e);
      setText(msg);
    } finally {
      setSending(false);
      qc.invalidateQueries({ queryKey: key });
    }
  }

  const usage = hist.data?.usage;
  return (
    <aside className="assistant-panel" aria-label="Setup assistant">
      <div className="assistant-head">
        <b>Setup assistant</b>
        <span className="spacer" />
        {messages.length > 0 && <button className="link small" onClick={async () => { await api(`${base}/assistant`, { method: "DELETE" }); qc.invalidateQueries({ queryKey: key }); }}>New chat</button>}
        <button className="link" onClick={onClose} aria-label="Close assistant">Close</button>
      </div>
      <div className="assistant-log" ref={log}>
        {messages.length === 0 && !hist.isLoading && (
          <div className="assistant-intro">
            <p>Tell me what you want set up and I'll prepare it. You check each change and press <b>Apply</b>; I never change anything on my own.</p>
            <div className="assistant-examples">{examples.map((x) => <button key={x} className="chip" onClick={() => send(x)}>{x}</button>)}</div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`assistant-msg ${m.role}`}>
            <div className="bubble" dir="auto">{m.content}</div>
            {m.cards.map((c, i) => <CardView key={i} id={`${m.id}:${i}`} card={c} applied={applied.includes(`${m.id}:${i}`)} onApplied={markApplied} />)}
          </div>
        ))}
        {sending && <div className="assistant-msg assistant"><div className="bubble muted">Thinking…</div></div>}
      </div>
      <ErrorText error={error} />
      <form className="assistant-form" onSubmit={(e) => { e.preventDefault(); send(text); }}>
        <textarea value={text} maxLength={2000} rows={2} placeholder="e.g. We're open 9 to 5 with a lunch break" onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(text); } }} aria-label="Message the assistant" />
        <button type="submit" disabled={sending || !text.trim()}>Send</button>
      </form>
      {usage && usage.limit != null && <p className="muted small assistant-usage">{usage.used} of {usage.limit} messages this month</p>}
    </aside>
  );
}

function CardView({ id, card, applied, onApplied }: { id: string; card: Card; applied: boolean; onApplied: (id: string) => void }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const email = useEmail();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [dismissed, setDismissed] = useState(false);

  if (card.type === "link" && card.page && pageRoute[card.page]) {
    return <div className="assistant-card"><Link to={`/o/${orgId}/${pageRoute[card.page]}`}>Open {card.label} →</Link></div>;
  }
  if (card.type === "connect" && card.connect === "whatsapp") {
    return <div className="assistant-card"><b>Connect WhatsApp</b><p className="small muted">Connect your WhatsApp Business number under Channels.</p><Link className="button-link" to={`/o/${orgId}/channels`}>Go to Channels</Link></div>;
  }
  if (card.type === "connect" && card.connect === "email") {
    const ov = email.data;
    const connect = async (provider: string, kind: string) => {
      setBusy(true); setError(null);
      try {
        const out = await api<{ url: string }>(`${base}/email/connect`, { method: "POST", json: { provider, kind } });
        window.location.assign(out.url);
      } catch (e) { setError(e); setBusy(false); }
    };
    return (
      <div className="assistant-card">
        <b>Connect your email</b>
        {!ov ? <Loading rows={2} /> : ov.providers.length === 0 ? <p className="small muted">Email connections aren't set up on this SVENEX server yet.</p> : (
          <div className="assistant-actions">
            {ov.providers.map((p) => <button key={p} disabled={busy} onClick={() => connect(p, "personal")}>Connect my {providerName[p]}</button>)}
            {ov.can_connect_shared && ov.providers.map((p) => <button key={p + "s"} className="secondary" disabled={busy} onClick={() => connect(p, "shared")}>{providerName[p]} company mailbox</button>)}
          </div>
        )}
        <ErrorText error={error} />
      </div>
    );
  }
  const req = card.request;
  if (card.type !== "change" || !req || !allowed.has(`${req.method} ${req.path}`)) return null;
  if (dismissed) return <div className="assistant-card muted small">Not applied.</div>;

  async function apply() {
    if (!req) return;
    setBusy(true); setError(null);
    try {
      let body: Record<string, unknown> = req.body;
      if (req.merge) {
        const cur = await api<Record<string, Record<string, unknown>>>(`${base}/${req.merge.from}`);
        let start: Record<string, unknown> = { ...(cur[req.merge.pick] ?? {}) };
        if (req.merge.keep) start = Object.fromEntries(Object.entries(start).filter(([k]) => req.merge!.keep!.includes(k)));
        body = { ...start, ...req.body };
        if (req.merge.wrap) body = { [req.merge.wrap]: body };
      }
      await api(`${base}/${req.path}`, { method: req.method, json: body });
      onApplied(id);
      qc.invalidateQueries({ queryKey: [orgId] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={`assistant-card change${applied ? " done" : ""}`}>
      <b>{card.title}</b>
      <ul>{(card.lines ?? []).map((l, i) => <li key={i} dir="auto">{l}</li>)}</ul>
      {applied ? <p className="small applied">✓ Applied</p> : (
        <div className="assistant-actions">
          <button disabled={busy} onClick={apply}>{busy ? "Applying…" : "Apply"}</button>
          <button className="link" disabled={busy} onClick={() => setDismissed(true)}>Not now</button>
        </div>
      )}
      <ErrorText error={error} />
    </div>
  );
}
